import { NgClass } from '@angular/common';
import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, finalize } from 'rxjs';

import { AuthUser } from '@oneComply/core/models/auth.model';
import { AdminNotification } from '@oneComply/core/models/notification.model';
import {
  ADMIN_ROUTE_COMMANDS,
  ADMIN_ROUTE_PATHS,
} from '@oneComply/core/constants/admin-route-paths';
import { AuthService } from '@oneComply/core/services/auth.service';
import { AdminNotificationsService } from '@oneComply/core/services/admin-notifications.service';
import { ProfileService } from '@oneComply/core/services/profile.service';
import { BootstrapIcon } from '@oneComply/shared/components/bootstrap-icon';
import { UserAvatar } from '@oneComply/shared/components/user-avatar/user-avatar';

interface HeaderUser {
  name: string;
  email: string;
  role: string;
  avatarUrl?: string;
}

const FALLBACK_HEADER_USER: HeaderUser = {
  name: 'Usuário',
  email: '',
  role: 'Usuário',
};

const HEADER_PREVIEW_LIMIT = 5;

@Component({
  selector: 'app-header',
  imports: [RouterLink, NgClass, BootstrapIcon, UserAvatar],
  templateUrl: './admin-header.html',
})
export class AdminHeader implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly profileService = inject(ProfileService);
  private readonly adminNotificationsService = inject(AdminNotificationsService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly sidebarCollapsed = input(false);
  readonly applicationTitle = input('Área administrativa');
  readonly toggleSidebar = output<void>();
  readonly logout = output<void>();

  protected readonly isMobileNavOpen = signal(false);
  protected readonly menuState = signal<Record<string, boolean>>({});
  protected readonly currentUser = signal<HeaderUser>({ ...FALLBACK_HEADER_USER });
  protected readonly adminRoutePaths = ADMIN_ROUTE_PATHS;
  protected readonly notifications = signal<AdminNotification[]>([]);
  protected readonly unreadCount = signal(0);
  protected readonly notificationsLoading = signal(false);
  protected readonly dismissingUuid = signal<string | null>(null);
  protected readonly isCompanyAdmin = signal(false);

  ngOnInit(): void {
    this.syncUserFromStorage();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.syncUserFromStorage();
        this.refreshUnreadCount();
      });

    if (this.authService.getToken()) {
      this.profileService
        .getMe()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.authService.updateStoredUser({
              name: response.data.name,
              email: response.data.email,
            });
            this.isCompanyAdmin.set(response.data.role === 'admin');
            this.currentUser.set(this.toHeaderUser(response.data));
          },
          error: () => this.syncUserFromStorage(),
        });

      this.refreshUnreadCount();
    }

    this.adminNotificationsService.unreadCount$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count) => this.unreadCount.set(count));
  }

  private syncUserFromStorage(): void {
    const storedUser = this.authService.getStoredUser();

    if (!storedUser) {
      return;
    }

    this.currentUser.set(this.toHeaderUser(storedUser));
  }

  private toHeaderUser(user: AuthUser | { name: string; email: string; role?: string }): HeaderUser {
    const name = user.name.trim();
    const email = user.email.trim();
    const role = 'role' in user && user.role === 'admin' ? 'Administrador' : 'Usuário';

    if (!name && !email) {
      return { ...FALLBACK_HEADER_USER };
    }

    return {
      name: name || FALLBACK_HEADER_USER.name,
      email,
      role,
    };
  }

  protected handleToggleSidebar(): void {
    this.toggleSidebar.emit();
  }

  protected handleToggleMobileNav(): void {
    this.isMobileNavOpen.update((open) => !open);
  }

  protected handleCloseMobileNav(): void {
    this.isMobileNavOpen.set(false);
  }

  protected handleOpenNotificationsFromMobile(): void {
    this.handleCloseMobileNav();
    void this.router.navigate(ADMIN_ROUTE_COMMANDS.notifications);
  }

  protected handleLogoutFromMobile(): void {
    this.handleCloseMobileNav();
    this.handleLogout();
  }

  protected handleToggleMenu(menuKey: string): void {
    const willOpen = !this.menuState()[menuKey];

    this.menuState.update((state) => ({
      ...state,
      [menuKey]: willOpen,
    }));

    if (menuKey === 'notifications' && willOpen) {
      this.loadHeaderNotifications();
    }
  }

  protected isMenuOpen(menuKey: string): boolean {
    return !!this.menuState()[menuKey];
  }

  protected handleCloseMenu(menuKey: string): void {
    if (this.menuState()[menuKey]) {
      this.menuState.update((state) => ({ ...state, [menuKey]: false }));
    }
  }

  protected handleDismissNotification(notification: AdminNotification, event: Event): void {
    event.stopPropagation();

    if (notification.is_read) {
      return;
    }

    this.dismissingUuid.set(notification.uuid);

    this.adminNotificationsService
      .markAsRead(notification.uuid)
      .pipe(finalize(() => this.dismissingUuid.set(null)))
      .subscribe({
        next: () => {
          this.notifications.update((items) =>
            items.filter((item) => item.uuid !== notification.uuid),
          );
        },
      });
  }

  protected handleOpenNotification(notification: AdminNotification): void {
    this.handleCloseMenu('notifications');

    if (!notification.is_read) {
      this.adminNotificationsService.markAsRead(notification.uuid).subscribe();
    }

    if (notification.report_uuid) {
      void this.router.navigate(
        ADMIN_ROUTE_COMMANDS.activeReportDetails(notification.report_uuid),
      );
    }
  }

  protected handleViewAllNotifications(): void {
    this.handleCloseMenu('notifications');
    void this.router.navigate(ADMIN_ROUTE_COMMANDS.notifications);
  }

  protected handleLogout(): void {
    this.handleCloseMenu('actions');
    this.logout.emit();
  }

  protected getUserSubtitle(): string {
    const user = this.currentUser();
    const email = user.email.trim();
    const role = user.role.trim();

    if (email && role) {
      return `${email} • ${role}`;
    }

    return email || role || '-';
  }

  private refreshUnreadCount(): void {
    if (!this.authService.getToken()) {
      return;
    }

    this.adminNotificationsService.refreshUnreadCount();
  }

  private loadHeaderNotifications(): void {
    if (!this.authService.getToken()) {
      return;
    }

    this.notificationsLoading.set(true);

    this.adminNotificationsService
      .list({ limit: HEADER_PREVIEW_LIMIT, offset: 0, unread_only: true })
      .pipe(finalize(() => this.notificationsLoading.set(false)))
      .subscribe({
        next: (response) => this.notifications.set(response.items),
      });
  }

  @HostListener('document:click', ['$event'])
  protected handleDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const actionsMenu = document.getElementById('header-actions-menu');
    const actionsToggle = document.getElementById('header-actions-menu-toggle');

    if (
      this.isMenuOpen('actions') &&
      actionsMenu &&
      actionsToggle &&
      !actionsMenu.contains(target) &&
      !actionsToggle.contains(target)
    ) {
      this.handleCloseMenu('actions');
    }

    const notificationsMenu = document.getElementById('header-notifications-menu');
    const notificationsToggle = document.getElementById('header-notifications-menu-toggle');

    if (
      this.isMenuOpen('notifications') &&
      notificationsMenu &&
      notificationsToggle &&
      !notificationsMenu.contains(target) &&
      !notificationsToggle.contains(target)
    ) {
      this.handleCloseMenu('notifications');
    }

    const mobileNav = document.getElementById('header-mobile-nav');
    const mobileNavToggle = document.getElementById('header-mobile-nav-toggle');

    if (
      this.isMobileNavOpen() &&
      mobileNav &&
      mobileNavToggle &&
      !mobileNav.contains(target) &&
      !mobileNavToggle.contains(target)
    ) {
      this.handleCloseMobileNav();
    }
  }

  @HostListener('window:resize')
  protected handleWindowResize(): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (window.innerWidth >= 768 && this.isMobileNavOpen()) {
      this.isMobileNavOpen.set(false);
    }
  }
}
